import { useEffect, useMemo, useState } from 'react'
import { View } from 'react-native'
import styled, { useTheme } from 'styled-components'
import { useUpdateEffect } from 'usehooks-ts'
import Icon from '../../../elements/images/Icon'
import { Text } from '../../../elements/typography'
import { BigPlayButton } from './BigPlayButton/BigPlayButton'
import { CaptionsModal } from './CaptionsModal/CaptionsModal'
import { ConfigurablePromos } from './ConfigurablePromos/ConfigurablePromos'
import FeaturedActorList from './FeaturedActors/ListDesktop/FeaturedActorList'
import { NextEpisode } from './NextEpisode/NextEpisode'
import { NextEpisodeControl } from './NextEpisode/NextEpisodeControl.js'
import { NextRecommendation } from './NextRecommendation/NextRecommendation.js'
import { PlaybackRateModal } from './PlaybackRateModal/PlaybackRateModal'
import Button, { desktopBtnSize } from './PlayerControls/Button'
import { Controls } from './PlayerControls/PlayerControls'
import { TrickPlayProgressBar } from './TrickPlayProgressBar/TrickPlayProgressBar.js'
import { VolumeControls } from './VolumeControls/VolumeControls'

const VideoTitle = styled(View)`
  margin: 0 auto 0 ${(props) => props.theme.spacing.md}px;
  justify-content: center;
  flex-shrink: 1;
`

const Title = styled(Text)`
  color: ${(props) => props.theme.colors.text};
  font-size: ${(props) => props.theme.fontSizes.xlarge * 1.2}px;
`

const LeftBody = styled(View)`
  flex: 1;
  align-items: flex-start;
`

const TopBodySection = styled(View)`
  flex: 1;
`

const BottomBodySection = styled(View)`
  flex: 1;
  justify-content: flex-end;
  align-items: ${(props) => props.alignItems ?? 'stretch'};
`

const RightBody = styled(View)`
  flex: 1;
  justify-content: flex-end;
  align-items: flex-end;
`

// The Desktop controls are only used for web, so gradient styles are OK.
const shadedControlsStyle = {
  backgroundImage: 'linear-gradient(90deg, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0) 40%)',
  backgroundColor: 'transparent',
}
const shadedFooterStyle = {
  backgroundImage:
    'linear-gradient(0deg, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.3) 60%, rgba(0,0,0,0) 100%)',
}

const DesktopPlayerControls = ({
  type,
  contentType,
  title,
  bigPlayButton,
  controlsShown,
  // onRequestHideControls,
  // onUserActive,
  onRequestClose,
  fullscreen,
  onFullscreenPress,
  onScrubbingStart,
  onScrubbingEnd,
  onSkipBack,
  onSkipForward,
  playing,
  onPlay,
  onPause,
  onPlayPause,
  muted,
  onToggleMute,
  volume,
  onVolumeChange,
  subtitles,
  activeSubtitles,
  onSubtitlesChosen,
  creditsReached,
  nextRecommendation,
  nextEpisode,
  actors,
  onActorSelect,
  scrubbingDisabled,
  progressTrackColor,
  playerWidth,
  playerHeight,
  toggleFullChat,

  setPlaybackRate,
  playbackRateOptions,
  playbackRate,

  pauseOverlayLeft,
  pauseOverlayRight,

  filmSlug,
  festivalId,
}) => {
  const [hasStarted, setHasStarted] = useState(playing)
  useEffect(() => {
    if (!hasStarted && playing) setHasStarted(true)
  }, [hasStarted, playing])

  const [visibleModal, setVisibleModal] = useState(null)
  const toggleModal = (name) => setVisibleModal(visibleModal !== name && name)

  const [watchCredits, setWatchCredits] = useState(false)
  useUpdateEffect(() => {
    if (!creditsReached) setWatchCredits(false) // Reset if scrubbed back from credits
  }, [creditsReached])

  const theme = useTheme()
  const iconSize = desktopBtnSize(theme.fontSizes.medium)

  const pauseThen = useMemo(() => {
    const makeHandler =
      (callback) =>
      (...args) => {
        onPause()
        callback(...args)
      }

    return {
      actorSelect: makeHandler(onActorSelect),
    }
  }, [onActorSelect, onPause])

  const visible = controlsShown || creditsReached
  return (
    <Controls
      visible={visible}
      style={[visible ? shadedControlsStyle : null, { width: playerWidth, height: playerHeight }]}
    >
      <Controls.Header>
        {onRequestClose && (
          <Button onPress={onRequestClose}>
            <Icon name="back" size={iconSize} />
          </Button>
        )}
      </Controls.Header>

      <Controls.Body row onPress={onPlayPause}>
        <LeftBody>
          <TopBodySection>
            {onActorSelect && (
              <FeaturedActorList actors={actors} onActorSelect={pauseThen.actorSelect} />
            )}
          </TopBodySection>

          <BottomBodySection alignItems="flex-end">
            <ConfigurablePromos
              promos={pauseOverlayLeft}
              filmSlug={filmSlug}
              festivalId={festivalId}
              onRequestPause={onPause}
              position="left"
            />
          </BottomBodySection>
        </LeftBody>

        <RightBody>
          <ConfigurablePromos
            promos={pauseOverlayRight}
            filmSlug={filmSlug}
            contentType={contentType}
            festivalId={festivalId}
            onRequestPause={onPause}
            position="right"
          />

          {creditsReached && !nextEpisode && nextRecommendation && (
            <NextRecommendation nextRecommendation={nextRecommendation} posterWidth={96} />
          )}

          {creditsReached && nextEpisode && !watchCredits && (
            <NextEpisode nextEpisode={nextEpisode} onWatchCredits={() => setWatchCredits(true)} />
          )}
        </RightBody>
      </Controls.Body>

      <Controls.Footer style={shadedFooterStyle}>
        <TrickPlayProgressBar
          onSlidingStart={onScrubbingStart}
          onSlidingComplete={onScrubbingEnd}
          scrubbingDisabled={scrubbingDisabled}
          progressTrackColor={progressTrackColor}
          inverted={theme.isRTL}
          showHoverMarker
        />

        <Controls.Buttons>
          <Button onPress={onPlayPause}>
            <Icon name={playing ? 'pause' : 'play'} size={iconSize} />
          </Button>

          {!scrubbingDisabled && (
            <Button onPress={onSkipBack}>
              <Icon name="replay10" size={iconSize} />
            </Button>
          )}

          {!scrubbingDisabled && (
            <Button onPress={onSkipForward}>
              <Icon name="forward10" size={iconSize} />
            </Button>
          )}

          <VolumeControls
            muted={muted}
            toggleMute={onToggleMute}
            volume={volume}
            onChange={onVolumeChange}
            iconSize={iconSize}
          />

          <VideoTitle>
            <Title numberOfLines={1} ellipsizeMode="tail">
              {title}
            </Title>
          </VideoTitle>

          {nextEpisode && <NextEpisodeControl nextEpisode={nextEpisode} iconSize={iconSize} />}

          {Boolean(subtitles && subtitles.length) && (
            <View>
              <Button onPress={() => toggleModal('captions')}>
                <Icon name="closed-caption" size={iconSize} />
              </Button>
              {visibleModal === 'captions' && (
                <CaptionsModal
                  dropUp
                  subtitles={subtitles}
                  activeSubtitles={activeSubtitles}
                  onSubtitlesChosen={onSubtitlesChosen}
                  onRequestClose={() => setVisibleModal(null)}
                />
              )}
            </View>
          )}

          {!scrubbingDisabled && setPlaybackRate && (
            <>
              <Button onPress={() => setVisibleModal(() => toggleModal('playbackRate'))}>
                <Icon name="speed" size={iconSize} />
              </Button>
              {visibleModal === 'playbackRate' && (
                <PlaybackRateModal
                  dropUp
                  playbackRate={playbackRate}
                  playbackRateOptions={playbackRateOptions}
                  setPlaybackRate={setPlaybackRate}
                  onRequestClose={() => setVisibleModal(null)}
                />
              )}
            </>
          )}

          {toggleFullChat && (
            <Button onPress={toggleFullChat}>
              <Icon name="chat" size={iconSize} />
            </Button>
          )}

          <Button onPress={onFullscreenPress}>
            <Icon name={fullscreen ? 'fullscreen-exit' : 'fullscreen'} size={iconSize} />
          </Button>
        </Controls.Buttons>
      </Controls.Footer>

      {!hasStarted && bigPlayButton && <BigPlayButton onPress={onPlay} />}
    </Controls>
  )
}

DesktopPlayerControls.controlsHeight = 140

export default DesktopPlayerControls
